import { useChatboxStore } from "../../store";
import type { MessagingMessage } from "@/model/messaging";
import { cn } from "@deputy-ai/common";
import { useTranslations } from "next-intl";

interface ToolCall {
  id: string;
  name: string;
  type: string;
  args: Record<string, any>;
}

interface ToolCallData {
  flow_id?: string;
  flow_name?: string;
  node_id?: string;
  node_name?: string;
  response_metadata?: Record<string, any>;
  tool_call_id?: string;
  tool_calls?: ToolCall[];
}

interface ToolCallMessageProps {
  message: MessagingMessage;
}

export const ToolCallMessage = ({ message }: ToolCallMessageProps) => {
  const {
    use: { options },
  } = useChatboxStore();
  if (!options?.debugMode?.enabled) {
    return;
  }
  return (
    <div className="flex flex-col gap-2 text-sm">
      <ToolCallRequest message={message} />
      <ToolCallResponse message={message} />
    </div>
  );
};

export const ToolCallRequest = ({ message }: ToolCallMessageProps) => {
  const data = message.payload.data as ToolCallData;
  const t = useTranslations("chatbox.content.toolCall");
  if (!data.tool_calls || data.tool_calls.length === 0) {
    return;
  }
  return (
    <>
      <span className="font-bold">{t("invokeToolCalls")}</span>
      {data.tool_calls.map((toolCall) => (
        <TookCallInvoke key={toolCall.id} toolCall={toolCall} />
      ))}
      {data.response_metadata && (
        <ToolCallRow
          className="border-t py-1"
          tlabel="toolCallMetadata"
          value={data.response_metadata}
        />
      )}
    </>
  );
};

export const ToolCallResponse = ({ message }: ToolCallMessageProps) => {
  const data = message.payload.data as ToolCallData;
  const t = useTranslations("chatbox.content.toolCall");
  if (!data.tool_call_id) {
    return;
  }
  return (
    <>
      <span className="font-bold">{t("toolCallResponse")}</span>
      <ToolCallRow tlabel="toolCallId" value={data.tool_call_id} />
      <ToolCallRow tlabel="toolCallResult" value={message.payload.message} />
    </>
  );
};

const TookCallInvoke = ({ toolCall }: { toolCall: ToolCall }) => {
  return (
    <div className="flex flex-col gap-1 border-t py-1">
      <ToolCallRow tlabel="toolCallId" value={toolCall.id} />
      <ToolCallRow tlabel="toolCallName" value={toolCall.name} />
      <ToolCallRow tlabel="toolCallType" value={toolCall.type} />
      <ToolCallRow tlabel="toolCallArgs" value={toolCall.args} />
    </div>
  );
};

const ToolCallRow = ({
  tlabel,
  value,
  className,
}: {
  tlabel: string;
  value: string | Record<string, any>;
  className?: string;
}) => {
  const t = useTranslations("chatbox.content.toolCall");
  const typ = typeof value === "string" ? "text" : "json";
  return (
    <div className={cn("flex gap-1", className)}>
      <span className="w-40 shrink-0">{t(tlabel)}</span>
      {typ === "json" && (
        <pre>
          <code>{JSON.stringify(value, null, 2)}</code>
        </pre>
      )}
      {typ === "text" && <span>{value as string}</span>}
    </div>
  );
};
